import React from 'react'

import { LightbulbIcon, MapPinIcon, XIcon } from 'lucide-react'
import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  IPermitTypeField,
  IPreciseLocation,
  ISiteLocation,
} from '~/client/graph/types.generated'
import { Button } from '~/client/src/shared/components/Button/Button'
import { DeleteIcon } from '~/client/src/shared/components/icons/DeleteIcon'
import { InfoIcon } from '~/client/src/shared/components/icons/InfoIcon'
import { LocationOffIcon } from '~/client/src/shared/components/icons/LocationOffIcon'
import { LocationPinIcon } from '~/client/src/shared/components/icons/LocationPinIcon'
import { MyLocationIcon } from '~/client/src/shared/components/icons/MyLocationIcon'
import MarkupEditor from '~/client/src/shared/components/MapMarkups/MarkupEditor'
import {
  ICameraViewport,
  IMarkupEditorHandles,
  MarkupLocationSnapshot,
} from '~/client/src/shared/components/MapMarkups/MarkupEditorStore'
import { MarkupEditorTextChannel } from '~/client/src/shared/components/MapMarkups/markupEditorTextChannel'
import MovePinControls from '~/client/src/shared/components/MovePinControls/MovePinControls'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import { UnsavedChangesDialog } from '~/client/src/shared/components/UnsavedChangesDialog/UnsavedChangesDialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { preciseLocationZoom } from '~/client/src/shared/map/mapZoomRule'
import GlobeView from '~/client/src/shared/models/GlobeView'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import GlobeViewControlStore from '~/client/src/shared/stores/GlobeViewControl.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { createMarkupsVisibilityKeyHandler } from '~/client/src/shared/utils/createMarkupsVisibilityKeyHandler'
import { createPinMoveKeyHandler } from '~/client/src/shared/utils/createPinMoveKeyHandler'
import { getFullscreenElement } from '~/client/src/shared/utils/fullscreenElement'
import {
  claimKeyboard,
  isClaimOnTop,
  KeyboardClaim,
  releaseKeyboard,
} from '~/client/src/shared/utils/keyboardClaim'
import {
  clearSnapshotUrls,
  decideSnapshotAction,
  hasCompleteSnapshot,
  hasPreciseCoordinates,
  mergeSnapshotResult,
} from '~/client/src/shared/utils/locationSnapshot'
import { toast } from '~/client/src/shared/utils/toast'

import { formatCoordinates } from '../../../../MapBoxViewer/formatCoordinates'
import MapBoxViewerStore from '../../../../MapBoxViewer/MapBoxViewer.store'
import SitemapAttributeTag from '../../../../SitemapAttributeTag/SitemapAttributeTag'
import MapViewsGallery from '../../../../SitemapsGallery/MapViewsGallery'
import SitePermitCreationFormStore from '../../../WorkflowCreationForm.store'
import {
  captureMapScreenshots,
  CAPTURE_FAILED_MESSAGE,
  CAPTURE_HEIGHT,
  CAPTURE_WIDTH,
} from './captureMapScreenshots'
import FormPreciseLocationMap, {
  IPreciseLocationPinState,
} from './map/FormPreciseLocationMap'
import {
  resolveDefaultGlobeId,
  resolvePinGlobeId,
  resolvePreciseLocationGlobeIds,
  resolveViewportForPreciseLocation,
  shouldRecomputeDefaultGlobe,
  waitForCaptureReadyMap,
} from './preciseLocationHelpers'
import { isPreciseLocationOutsideClick } from './preciseLocationOutsideClick'
import { startViewportTracking } from './viewportChangeTracker'

interface IProps {
  value: ISiteLocation
  permitType?: WorkflowType
  store?: SitePermitCreationFormStore
  typeField?: IPermitTypeField
  isViewOnly?: boolean

  state?: InitialState
  globeViewsStore?: GlobeViewsStore
  eventsStore?: EventsStore
  sitemapItemsStore?: SitemapItemsStore
  locationAttributesStore?: LocationAttributesStore
  basemapsStore?: BasemapsStore
  tilesetsStore?: TilesetsStore
  sitemapsStore?: SitemapsStore
  isMobileEnv?: boolean
  saveLocation?: (value: ISiteLocation) => void
  fileUploadingStore?: FileUploadingStore
}

const DEFAULT_ICON_SIZE = 16
const ASSOCIATE_LOCATION_TEXT =
  'Do you want to associate this Pin location with'
const KEEP_PREVIOUS_LOCATION_TEXT = 'No, keep previous location'

const COLLAPSED_MAP_HEIGHT = 120
const GEOLOCATION_TIMEOUT = 10000
const GEOLOCATION_MAX_AGE = 60000
const GEOLOCATION_ZOOM_LEVEL = 18
const MAP_CAPTURE_WAIT_MS = 5000
// Other Workflows' text is drawn over this Workflow's shapes, so a click on it copies it
// instead of selecting the shape beneath.
const REF_TEXT_LAYER_IDS = ['markup-ref-text']

@inject(
  'state',
  'globeViewsStore',
  'eventsStore',
  'sitemapItemsStore',
  'locationAttributesStore',
  'basemapsStore',
  'tilesetsStore',
  'sitemapsStore',
  'store',
  'isMobileEnv',
  'fileUploadingStore',
)
@observer
export default class WorkflowFormPreciseLocation extends React.Component<IProps> {
  private mapBoxViewerStore: MapBoxViewerStore
  public globeViewControlStore: GlobeViewControlStore
  private containerRef = React.createRef<HTMLDivElement>()
  private insideTreeEvent: MouseEvent = null
  // Fullscreen target — keeps the toolbar/pills in while the header/footer (its
  // siblings) drop out.
  private stageRef = React.createRef<HTMLDivElement>()
  // Derived from the shared fullscreen observable — true only while the stage
  // itself is the fullscreen element (observer re-renders on change).
  private get isBrowserFullscreen(): boolean {
    return (
      !!this.stageRef.current &&
      getFullscreenElement() === this.stageRef.current
    )
  }
  @observable private closestLocation: LocationBase = null
  @observable private value: ISiteLocation = null
  @observable private isExpanded: boolean = false
  @observable private isEditMode: boolean = false
  @observable private isMarkupMode: boolean = false
  // Latched true on the first markup interaction (editor goes non-idle) or an
  // explicit Move; collapses the "Move" pill to the corner badge for the session.
  @observable private isMoveAffordanceCollapsed: boolean = false
  // The map center frozen when the user finishes positioning the pin ("Done"),
  // held until the deferred Save. The fixed pin renders here (a real geo coord,
  // glued to the ground) so panning the map in markup mode doesn't drag it.
  @observable private pendingViewport: ICameraViewport | null = null
  @observable private hasUnsavedChanges: boolean = false
  @observable private hasUnsavedEditorChanges: boolean = false
  @observable private showMarkupConfirmationDialog: boolean = false
  @observable private permitType: WorkflowType = null
  @observable private localProps: IProps = null
  // The map registers ~80 label images and 30 layers on mount, blocking the frame the form opens
  // in (STX1-1127). Latched true and never cleared: a remount would reset the viewport.
  @observable private canMountMap: boolean = false
  private mapMountTimer: number = null
  private wasExpanded: boolean = false
  private initialViewport: ICameraViewport = null
  private initialGlobeId: string = null
  private collapsedZoom: number = null
  private viewportReactionDisposer: (() => void) | null = null
  // Written beside isExpanded so it is in place before the children that read it
  // render. .ref, or MobX proxies it and breaks the claim stack's identity check.
  @observable.ref private keyboardClaim: KeyboardClaim | null = null
  // Unique key for the capture registry — stable per component instance
  private readonly captureRegistryKey = `${Date.now()}-${Math.random()}`

  private get isDesktopFullscreenMode(): boolean {
    return this.isExpanded && !this.localProps.isMobileEnv
  }

  private get isMobileExpandedMode(): boolean {
    return this.isExpanded && this.localProps.isMobileEnv
  }

  private get expandedTitle(): string {
    return this.isEditMode
      ? Localization.translator.editPreciseLocation
      : Localization.translator.preciseLocation
  }

  private get isCollapsedMode(): boolean {
    return !this.isExpanded
  }

  @computed
  private get sitemapItemForValue() {
    const id = this.value?.id
    if (!id) {
      return null
    }
    return this.localProps.sitemapItemsStore.byAssignedId.get(id) ?? null
  }

  @computed
  private get inheritedAncestor() {
    const id = this.value?.id
    if (!id) {
      return null
    }
    return this.localProps.globeViewsStore.resolveLocationCoordinates(id, {
      includeProject: false,
    })
  }

  @computed
  private get inheritedCoordinates() {
    return this.inheritedAncestor?.coordinates ?? null
  }

  @computed
  private get isLocationGeolocated(): boolean {
    return !!this.sitemapItemForValue?.coordinates
  }

  @computed
  private get hasInheritedFallback(): boolean {
    return !this.isLocationGeolocated && !!this.inheritedAncestor
  }

  // The location carries its own position, so nothing is inherited — but no pin was ever placed,
  // and the preview is otherwise indistinguishable from one that was.
  @computed
  private get hasUnplacedDefault(): boolean {
    return !this.hasPreciseLocation && this.isLocationGeolocated
  }

  @computed
  private get inheritedAncestorLocation() {
    const id = this.inheritedAncestor?.resolvedLocationId
    if (!id) {
      return null
    }
    return this.localProps.locationAttributesStore.getById(id) ?? null
  }

  private get hasPreciseLocation(): boolean {
    return (
      this.value?.preciseLocation?.latitude != null &&
      this.value?.preciseLocation?.longitude != null
    )
  }

  private get isAdminOrFormsMaster(): boolean {
    return !!this.localProps.state?.userActiveProjectSettings
      ?.isAdminOrFormsMaster
  }

  // A locked (Done/Denied) workflow is editable only by an admin; everyone else
  // gets view-only. New (creation) workflows have no existingPermit → not locked.
  private get isLockedWorkflow(): boolean {
    return !!this.localProps.store?.existingPermit?.isDoneOrDenied
  }

  private get canEdit(): boolean {
    return !this.isLockedWorkflow || this.isAdminOrFormsMaster
  }

  // Markups persist via their own mutation, so an admin/forms-master may edit
  // them on any workflow, including view-only ones — Accepted, Done, Denied,
  // etc. — not just locked ones. Everyone else needs an editable, non-locked form.
  private get canEditMarkups(): boolean {
    return (
      this.isAdminOrFormsMaster || (!this.localProps.isViewOnly && this.canEdit)
    )
  }

  // Location editing — the pin tool, the blue button, the pin-drag sub-mode, and
  // the collapsed move/delete controls — is allowed only where the change can
  // actually persist: not view-only and not locked. Locked workflows are
  // descoped: there's no location-only mutation, so an admin's location edit
  // couldn't be saved (unlike markups, which persist via their own mutation).
  // Markup editing is gated by canEditMarkups, so admins can still edit markups here.
  private get canEditLocation(): boolean {
    return !this.localProps.isViewOnly && !this.isLockedWorkflow
  }

  private get isPinLocationField(): boolean {
    const { typeField, store, saveLocation } = this.props
    return (
      !!saveLocation &&
      !!typeField &&
      typeField.id === store?.workflowFieldsStore.template?.locationField?.id
    )
  }

  private get pinCoordinates(): {
    latitude: number
    longitude: number
  } | null {
    // A pending (unsaved) pin move is frozen at the Done-time map center, not in
    // `value` yet — render the fixed pin there so it neither snaps back to the
    // last-saved spot nor follows the map when panned in markup mode.
    if (this.pendingViewport) {
      return {
        latitude: this.pendingViewport.latitude,
        longitude: this.pendingViewport.longitude,
      }
    }
    if (this.hasPreciseLocation) {
      return {
        latitude: this.value.preciseLocation.latitude,
        longitude: this.value.preciseLocation.longitude,
      }
    }
    const coords = this.inheritedCoordinates
    if (coords) {
      return { latitude: coords.latitude, longitude: coords.longitude }
    }
    return null
  }

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    this.globeViewControlStore = new GlobeViewControlStore(
      props.eventsStore,
      props.globeViewsStore,
      props.sitemapItemsStore,
      props.locationAttributesStore,
    )

    this.mapBoxViewerStore = new MapBoxViewerStore(
      props.sitemapsStore,
      props.tilesetsStore,
      props.sitemapItemsStore,
      props.locationAttributesStore,
      props.state,
      this.globeViewControlStore,
      props.basemapsStore,
      null,
    )
    this.mapBoxViewerStore.markupPrefs.setAdjacentMarkupsToggleEnabled(true)
    this.initializeState(props)
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    this.updateStateFromProps(prevProps)

    if (!this.props.isMobileEnv) {
      if (this.isExpanded && !this.wasExpanded) {
        document.addEventListener('mousedown', this.handleClickOutside)
      } else if (!this.isExpanded && this.wasExpanded) {
        document.removeEventListener('mousedown', this.handleClickOutside)
      }
    }
    this.wasExpanded = this.isExpanded
  }

  public componentDidMount() {
    // A timer, not rAF: rAF never fires in a background tab, which would leave the map unmounted
    // for the whole visit. Still lands after this commit paints, and before the idle camera move.
    this.mapMountTimer = window.setTimeout(() => this.mountMap(), 0)

    if (!this.props.isMobileEnv && this.isExpanded) {
      document.addEventListener('mousedown', this.handleClickOutside)
    }
    document.addEventListener('keydown', this.handlePinEditKeyDown)
    if (!this.props.isMobileEnv) {
      window.addEventListener('keydown', this.handleAdjacentMarkupsShortcut)
    }

    // Register a capture-and-apply function. Each instance captures its own
    // mini map AND writes the result directly to its own ISiteLocation value,
    // so table rows with different pins each get their own screenshot.
    if (this.props.store) {
      this.props.store.registerPreciseLocationCapture(
        this.captureRegistryKey,
        this.captureAndApplyScreenshot,
      )
    }
  }

  public componentWillUnmount() {
    window.clearTimeout(this.mapMountTimer)
    document.removeEventListener('mousedown', this.handleClickOutside)
    document.removeEventListener('keydown', this.handlePinEditKeyDown)
    window.removeEventListener('keydown', this.handleAdjacentMarkupsShortcut)
    // Unmounted while expanded — the map behind must get its keys back.
    this.releaseKeyboardClaim()
    this.disposeViewportReaction()
    this.props.store?.unregisterPreciseLocationCapture(this.captureRegistryKey)
    this.globeViewControlStore.dispose()
  }

  @action
  private mountMap(): void {
    this.canMountMap = true
  }

  // Cancel/Save in fullscreen end editing, so they drop out of fullscreen too.
  // No-op when not fullscreen, so the normal footer/X path is unaffected.
  private exitBrowserFullscreenIfActive(): void {
    if (this.isBrowserFullscreen) {
      document.exitFullscreen?.()
    }
  }

  // During creation the workflow has no id yet, so fall back to the creation
  // store's stable temp id — markups buffer under it and are flushed to the real
  // id once the workflow is created (see SitePermitCreationFormStore).
  private get markupSitePermitId(): string {
    return (
      this.localProps.store?.editablePermit?.id ||
      this.localProps.store?.markupDraftId
    )
  }

  private get isDeferredMarkup(): boolean {
    return !this.localProps.store?.editablePermit?.id
  }

  private get markupProjectId(): string {
    return this.localProps.state?.activeProject?.id
  }

  @action.bound
  private enterMarkupMode(): void {
    this.isMarkupMode = true
  }

  @action.bound
  private exitMarkupMode(): void {
    // Before closeMap restores the globe: the leaving editor reloads on it, and that reload must
    // not reach the map's session text.
    this.editorText.release()
    this.isMarkupMode = false
    this.isEditMode = false
    this.pendingViewport = null
    this.markupSave = null
    this.hasUnsavedEditorChanges = false
    this.copySelectedMarkupId = null
  }

  // Snapshot at pin-edit start so Cancel reverts only this drag, not to the saved pin.
  private pinEditEntry: {
    viewport: ICameraViewport | null
    hasUnsavedChanges: boolean
  } | null = null

  // The moved viewport stays pending until the unified Save.
  @action.bound
  private enterPinEdit(): void {
    this.pinEditEntry = {
      viewport: this.pendingViewport,
      hasUnsavedChanges: this.hasUnsavedChanges,
    }
    this.isEditMode = true
    this.isMoveAffordanceCollapsed = true
    this.startTrackingViewport()
    this.mapBoxViewerStore.focusMapCanvas()
  }

  // Re-invoking while pin-drag is active (the markup editor's Esc) cancels it and
  // reverts the in-progress location change; markups untouched.
  @action.bound
  private togglePinEdit(): void {
    if (this.isEditMode) {
      this.cancelPinEdit()
    } else {
      this.enterPinEdit()
    }
  }

  @action.bound
  private cancelPinEdit(): void {
    this.disposeViewportReaction()
    const entry = this.pinEditEntry
    this.pinEditEntry = null
    if (entry?.viewport) {
      const v = entry.viewport
      this.mapBoxViewerStore.mapRef?.flyTo({
        center: [v.longitude, v.latitude],
        zoom: v.zoom,
        pitch: v.pitch,
        bearing: v.bearing,
        essential: true,
      })
      this.pendingViewport = v
      this.hasUnsavedChanges = entry.hasUnsavedChanges
    } else {
      this.resetViewport(true)
      this.pendingViewport = null
      this.hasUnsavedChanges = false
    }
    this.isEditMode = false
  }

  @action.bound
  private exitPinEdit(): void {
    this.disposeViewportReaction()
    // Freeze the pin at the dragged-to map center so it stays glued to the
    // ground when the map is panned back in markup mode (and so Save persists it).
    if (this.hasUnsavedChanges) {
      const previous = this.getLocationSnapshot()
      const { latitude, longitude, bearing, pitch, zoom } =
        this.mapBoxViewerStore.viewport
      this.pendingViewport = { latitude, longitude, bearing, pitch, zoom }
      // Fold the move into the unified undo/redo timeline — but only when the pin
      // genuinely moved. A drag that returns to the committed spot is a no-op and
      // must not create a phantom undo step.
      if (JSON.stringify(this.pendingViewport) !== JSON.stringify(previous)) {
        this.markupRecordLocationChange?.()
      }
    }
    this.isEditMode = false
  }

  @action.bound
  private setMarkupSelectIdle(isIdle: boolean): void {
    if (!isIdle) this.isMoveAffordanceCollapsed = true
  }

  // Footer Save: commit markups, then persist the location only if the user
  // produced a new camera via "Edit Precise Location" → "Done" (pendingViewport).
  // Panning/rotating while editing markups never reaches here.
  @action.bound
  private saveEdit(): void {
    this.exitBrowserFullscreenIfActive()
    // Saving straight from the pin-drag sub-mode: finalize the pin first (same as
    // "Done") so its moved position is captured into pendingViewport.
    if (this.isEditMode) {
      this.exitPinEdit()
    }
    // Capture the frozen "Done" snapshot BEFORE markupSave: the editor's save
    // calls onClose (exitMarkupMode), which clears pendingViewport.
    const camera = this.pendingViewport
    // Markups are part of what the report snapshot shows — a drawing edit
    // invalidates it exactly like a pin move does. Read BEFORE markupSave,
    // whose onClose resets the dirty flag.
    if (this.hasUnsavedEditorChanges) {
      clearSnapshotUrls(this.value)
    }
    this.markupSave?.()
    if (camera) {
      this.saveMapLocation(camera)
    } else {
      this.exitMarkupMode()
      this.closeMap()
    }
  }

  // No preview step to fall back to, so Cancel closes the modal outright.
  @action.bound
  private cancelEdit(): void {
    if (this.hasUnsavedChanges) {
      this.disposeViewportReaction()
      this.resetViewport(true)
    }
    this.exitMarkupMode()
    this.hasUnsavedChanges = false
    this.closeMap()
  }

  @observable private copySelectedMarkupId: string | null = null
  // This Workflow's text on the map: the editor's while markup mode is on, the saved one otherwise.
  private readonly editorText = new MarkupEditorTextChannel()

  @action.bound
  private setCopySelectedMarkup(id: string | null): void {
    this.copySelectedMarkupId = id
  }

  private markupSave: (() => void) | null = null
  private markupRecordLocationChange: (() => void) | null = null
  // Pin location captured at open; the baseline that gates dirtiness as undo/redo
  // walks the unified timeline back and forth.
  private initialLocationSnapshot: MarkupLocationSnapshot = null

  @action.bound
  private registerMarkupSave(handles: IMarkupEditorHandles | null): void {
    this.markupSave = handles?.save ?? null
    this.markupRecordLocationChange = handles?.recordLocationChange ?? null
    // Teardown must keep the baseline, or it re-reads as the new clean state.
    if (!handles) {
      return
    }
    // Capture the dirtiness baseline at the same moment the editor captures its
    // own history[0] (onReady fires right after resetHistory), so the two
    // baselines can't diverge by mount timing.
    this.initialLocationSnapshot = this.getLocationSnapshot()
  }

  private getLocationSnapshot = (): MarkupLocationSnapshot => {
    return (
      this.pendingViewport ??
      this.viewportFromPreciseLocation(this.value?.preciseLocation) ??
      this.initialViewport ??
      null
    )
  }

  private viewportFromPreciseLocation(
    pl: ISiteLocation['preciseLocation'],
  ): MarkupLocationSnapshot {
    if (pl?.latitude == null || pl?.longitude == null) {
      return null
    }
    return {
      latitude: pl.latitude,
      longitude: pl.longitude,
      bearing: pl.bearing ?? 0,
      pitch: pl.pitch ?? 0,
      zoom: preciseLocationZoom(pl.zoom),
    }
  }

  // Re-applies a snapshot's location on undo/redo: moves the camera only on a real
  // change (so markup-only undos don't jump it) and re-derives dirtiness vs baseline.
  @action.bound
  private applyLocationSnapshot(loc: MarkupLocationSnapshot): void {
    // Ignore location restores while the pin is being actively dragged: pin-edit
    // owns the camera, and moving it here would desync the in-progress session.
    if (this.isEditMode) {
      return
    }
    const current = this.getLocationSnapshot()
    if (loc && JSON.stringify(loc) !== JSON.stringify(current)) {
      this.mapBoxViewerStore.jumpToViewport(loc)
    }
    const dirty =
      JSON.stringify(loc) !== JSON.stringify(this.initialLocationSnapshot)
    this.pendingViewport = dirty ? loc : null
    this.hasUnsavedChanges = dirty
  }

  @action.bound
  private setEditorDirty(dirty: boolean): void {
    this.hasUnsavedEditorChanges = dirty
  }

  /** Read by the capture path too: with the gallery unmounted, waiting for a map only burns the
   * timeout on a save that cannot capture anyway. */
  private get isMapSubtreeRendered(): boolean {
    return (
      !!this.hasPreciseLocation ||
      !!this.isLocationGeolocated ||
      !!this.inheritedAncestorLocation ||
      !!this.isExpanded
    )
  }

  public render() {
    if (!this.isMapSubtreeRendered) {
      return this.renderNonGeolocatedState()
    }

    return (
      <>
        {this.isCollapsedMode && (
          <>
            {this.closestLocation?.id &&
              this.closestLocation.id !== this.value?.id &&
              this.renderLocationAssociation()}
            {this.renderHeader()}
          </>
        )}

        {this.isExpanded && (
          <div className="tw:fixed tw:inset-0 tw:z-[99] tw:bg-black/50" />
        )}

        <div
          className={`bg-white ${
            this.isDesktopFullscreenMode
              ? 'tw:fixed tw:top-[80px] tw:left-[60px] tw:right-[60px] tw:bottom-[60px] tw:rounded-lg tw:overflow-hidden tw:z-[100] tw:flex tw:flex-col tw:pb-safe-3'
              : this.isMobileExpandedMode
                ? 'tw:fixed tw:top-[56px] tw:left-0 tw:right-0 tw:bottom-0 tw:z-[100] tw:flex tw:flex-col tw:rounded-t-[16px] tw:overflow-hidden tw:pb-safe-3'
                : 'relative mt8 mb8'
          }`}
          data-app-chrome={this.isMobileExpandedMode ? 'surface' : undefined}
          ref={this.containerRef}
          onMouseDownCapture={this.markInsideTree}
        >
          {this.isDesktopFullscreenMode && this.renderExpandedHeader()}

          {this.isMobileExpandedMode && this.renderMobileHeader()}

          {/* IMPORTANT: The map gallery MUST stay at this exact tree position
              across all modes (collapsed/desktop/mobile). Changing the nesting
              depth causes FormPreciseLocationMap to remount, which triggers a
              mapbox style reload that resets the viewport to zoom=0 (globe). */}
          <div
            ref={this.stageRef}
            className={`precise-location-stage${
              this.isExpanded ? ' tw:flex-1 tw:min-h-0 tw:relative' : ''
            }`}
            style={this.markupToolbarVars}
          >
            <div
              className={
                this.isCollapsedMode
                  ? 'full-width relative tw:border tw:border-border tw:rounded-t tw:overflow-hidden tw:[&_.center-pin-marker]:scale-75'
                  : this.isMobileExpandedMode
                    ? 'tw:h-full tw:overflow-hidden'
                    : 'tw:h-full'
              }
              style={
                this.isCollapsedMode
                  ? { height: COLLAPSED_MAP_HEIGHT }
                  : undefined
              }
            >
              <div className="full-width full-height overflow-hidden">
                {this.canMountMap && this.renderMapViewsGallery()}
              </div>
              {this.isCollapsedMode &&
                (this.hasInheritedFallback || this.hasUnplacedDefault) &&
                this.renderThumbnailStatusBadge()}
              {this.hasPreciseLocation &&
                this.isCollapsedMode &&
                this.renderReportPreviewTooltip()}
              {this.hasPreciseLocation &&
                this.isCollapsedMode &&
                this.canEditLocation &&
                this.renderDeleteButton()}
            </div>
            {(this.isDesktopFullscreenMode || this.isMobileExpandedMode) &&
              !this.isEditMode &&
              !this.isMarkupMode &&
              (this.hasInheritedFallback || this.hasUnplacedDefault) &&
              this.renderInheritedHintOverlay()}
            {this.isMarkupMode &&
              this.isExpanded &&
              !!this.markupSitePermitId && (
                <MarkupEditor
                  mapBoxViewerStore={this.mapBoxViewerStore}
                  sitePermitId={this.markupSitePermitId}
                  projectId={this.markupProjectId}
                  deferred={this.isDeferredMarkup}
                  onReady={this.registerMarkupSave}
                  getLocation={this.getLocationSnapshot}
                  onRestoreLocation={this.applyLocationSnapshot}
                  onDirtyChange={this.setEditorDirty}
                  onClose={this.exitMarkupMode}
                  copySelectedId={this.copySelectedMarkupId}
                  onCopySelect={this.setCopySelectedMarkup}
                  yieldToTextLayerIds={REF_TEXT_LAYER_IDS}
                  editorText={this.editorText}
                  showLocationTool={this.canEditLocation}
                  isLocationEditActive={this.isEditMode}
                  onEditPreciseLocation={this.togglePinEdit}
                  onIdleSelectChange={this.setMarkupSelectIdle}
                  keyboardClaim={this.keyboardClaim}
                  portalContainer={
                    this.isBrowserFullscreen ? this.stageRef.current : null
                  }
                />
              )}
            {this.isBrowserFullscreen && this.renderFullscreenActions()}
          </div>

          {this.isCollapsedMode &&
            (this.hasInheritedFallback
              ? this.renderInheritedHintBanner()
              : this.renderCollapsedBottomControls())}

          {(this.isDesktopFullscreenMode || this.isMobileExpandedMode) &&
            !this.isBrowserFullscreen &&
            this.renderFooter()}
        </div>

        <UnsavedChangesDialog
          open={this.showMarkupConfirmationDialog}
          title={Localization.translator.youHaveUnsavedChanges}
          onKeepEditing={this.dismissMarkupConfirmationDialog}
          onDiscard={this.discardMarkupsAndClose}
          onSave={this.saveMarkupsAndClose}
        />
      </>
    )
  }

  private renderMobileHeader(): JSX.Element {
    return (
      <div className="tw:flex tw:items-center tw:justify-between tw:px-3">
        <Button
          variant="ghost"
          size="icon"
          aria-label={Localization.translator.close}
          onClick={this.handleCloseAttempt}
          className="tw:text-muted-foreground"
        >
          <XIcon aria-hidden="true" />
        </Button>
        <span className="tw:text-sm tw:font-medium tw:leading-5 tw:text-foreground tw:tracking-tight">
          {this.expandedTitle}
        </span>
        <div className="tw:size-8" />
      </div>
    )
  }

  private renderLocationAssociation(): JSX.Element {
    return (
      <div className="ba-palette-grey brada4 mt10 mb10">
        {this.renderLocationAssociationText()}
        <div className="row y-center mb20 m5 x-end">
          {this.renderDismissSuggestLocationButton()}
          {this.renderSaveSuggestLocationButton()}
        </div>
      </div>
    )
  }

  private renderLocationAssociationText(): JSX.Element {
    return (
      <div className="text large m20">
        {ASSOCIATE_LOCATION_TEXT}
        <SitemapAttributeTag
          shouldShowAsTag={false}
          dataObject={this.closestLocation}
          iconSize={DEFAULT_ICON_SIZE}
          contentContainerClassName="text-ellipsis"
        >
          <span
            className="text large bold ellipsis parent-tag"
            title={this.closestLocation.name}
          >
            {this.closestLocation.name}
          </span>
        </SitemapAttributeTag>
        ?
      </div>
    )
  }

  private renderNonGeolocatedState(): JSX.Element {
    return (
      <div className="tw:flex tw:flex-col tw:gap-1 tw:py-2 tw:px-2">
        <div className="tw:flex tw:items-center tw:gap-1">
          <LocationOffIcon
            size={18}
            className="tw:text-muted-foreground tw:shrink-0"
          />
          <span className="tw:text-sm tw:leading-5 tw:text-muted-foreground tw:tracking-tight">
            {Localization.translator.thisLocationIsNotGeolocated}
          </span>
        </div>
        {!this.props.isViewOnly && (
          <div
            className="tw:flex tw:items-center tw:gap-1 tw:cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={this.openPopup}
            onKeyDown={this.handleSetPreciseLocationKeyDown}
          >
            <LocationPinIcon
              size={18}
              className="tw:text-primary tw:shrink-0"
            />
            <span className="tw:text-sm tw:leading-5 tw:text-muted-foreground tw:tracking-tight tw:underline">
              {Localization.translator.setPreciseLocation}
            </span>
          </div>
        )}
      </div>
    )
  }

  private renderExpandedHeader(): JSX.Element {
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:p-3 tw:border-b tw:border-border">
        <Button
          variant="ghost"
          size="icon"
          aria-label={Localization.translator.close}
          onClick={this.handleCloseAttempt}
          className="tw:text-muted-foreground"
        >
          <XIcon aria-hidden="true" />
        </Button>
        <span className="tw:text-base tw:font-semibold tw:leading-6 tw:text-foreground tw:tracking-[-0.176px]">
          {this.expandedTitle}
        </span>
      </div>
    )
  }

  private renderDeleteButton(): JSX.Element {
    return (
      <div className="tw:absolute tw:top-[7px] tw:right-[7px] tw:z-10 tw:bg-white tw:rounded">
        <button
          className="tw:flex tw:items-center tw:justify-center tw:p-1 tw:border-0 tw:bg-transparent tw:rounded tw:cursor-pointer tw:size-6 tw:transition-colors tw:hover:bg-muted tw:active:bg-accent"
          onClick={this.deletePreciseLocation}
        >
          <DeleteIcon size="xs" className="tw:text-destructive" />
        </button>
      </div>
    )
  }

  private renderReportPreviewTooltip(): JSX.Element {
    return (
      <div className="tw:absolute tw:top-[7px] tw:left-[7px] tw:z-10">
        <Tooltip>
          <TooltipTrigger
            type="button"
            className="tw:flex tw:items-center tw:justify-center tw:p-1 tw:bg-white/80 tw:rounded tw:size-6 tw:border-0 tw:cursor-default"
          >
            <LightbulbIcon
              aria-hidden="true"
              className="tw:size-4 tw:text-muted-foreground"
            />
          </TooltipTrigger>
          <TooltipContent side="right">
            {Localization.translator.preciseLocationPreviewTooltip}
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  private renderHeader(): JSX.Element {
    if (this.props.isViewOnly) return null

    const title = Localization.translator.preciseLocation

    return (
      <span
        className="text left large line-extra-large bold text-ellipsis light px8"
        title={title}
      >
        {title}
      </span>
    )
  }

  private renderAncestorTag(): JSX.Element {
    return this.renderLocationTag(this.inheritedAncestorLocation)
  }

  private renderLocationTag(ancestor: LocationBase): JSX.Element {
    if (!ancestor) {
      return null
    }
    return (
      <SitemapAttributeTag
        dataObject={ancestor}
        shouldShowAsTag={true}
        iconSize={DEFAULT_ICON_SIZE}
        contentContainerClassName="tw:truncate"
      >
        <span
          className="tw:text-sm tw:leading-5 tw:font-medium tw:truncate"
          title={ancestor.name}
        >
          {ancestor.name}
        </span>
      </SitemapAttributeTag>
    )
  }

  private renderInheritedHintOverlay(): JSX.Element {
    const location = this.inheritedAncestorLocation
    if (!location) {
      return null
    }
    const positionClass = this.isMobileExpandedMode
      ? 'tw:bottom-4 tw:left-4 tw:right-4'
      : 'tw:bottom-4 tw:left-1/2 tw:-translate-x-1/2'
    return (
      <div
        className={`tw:absolute tw:z-10 tw:bg-white tw:border tw:border-md3-outline-variant tw:rounded-lg tw:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.15),0px_1px_3px_0px_rgba(0,0,0,0.3)] tw:px-3 tw:py-2 tw:flex tw:items-center tw:gap-2 tw:min-w-0 ${positionClass}`}
      >
        <div className="tw:flex tw:items-center tw:gap-1 tw:min-w-0">
          <InfoIcon
            size="sm"
            className="tw:text-md3-on-surface-variant tw:shrink-0"
          />
          <span className="tw:shrink-0 tw:whitespace-nowrap tw:text-sm tw:font-medium tw:leading-5 tw:text-md3-on-surface tw:tracking-tight">
            {this.hasUnplacedDefault
              ? Localization.translator.defaultsTo
              : Localization.translator.inheritsFrom}
          </span>
        </div>
        {this.renderLocationTag(location)}
      </div>
    )
  }

  private renderThumbnailStatusBadge(): JSX.Element {
    const defaultsTo = this.hasUnplacedDefault
      ? this.inheritedAncestorLocation
      : null
    if (this.hasUnplacedDefault && !defaultsTo) {
      return null
    }
    const label = defaultsTo
      ? `${Localization.translator.defaultsTo} ${defaultsTo.name}`
      : Localization.translator.notGeolocated

    return (
      <div className="tw:absolute tw:top-1.5 tw:left-1.5 tw:z-10 tw:pointer-events-none tw:flex tw:items-center tw:gap-1 tw:bg-white tw:border tw:border-md3-outline-variant tw:rounded-full tw:px-1.5 tw:py-0.5 tw:shadow-sm tw:w-fit tw:max-w-[calc(100%-0.75rem)]">
        {defaultsTo ? (
          <MapPinIcon
            size={12}
            className="tw:text-md3-on-surface-variant tw:shrink-0"
          />
        ) : (
          <LocationOffIcon
            size={12}
            className="tw:text-md3-on-surface-variant tw:shrink-0"
          />
        )}
        <span className="tw:text-[11px] tw:leading-4 tw:font-medium tw:text-md3-on-surface-variant tw:tracking-tight tw:truncate">
          {label}
        </span>
      </div>
    )
  }

  private renderInheritedHintBanner(): JSX.Element {
    const ancestor = this.inheritedAncestorLocation
    if (!ancestor) {
      return (
        <div className="tw:flex tw:items-center tw:gap-2 tw:h-10 tw:pl-3 tw:pr-1.5 tw:py-1.5 tw:bg-white tw:border tw:border-md3-outline-variant tw:rounded-b tw:-mt-px tw:min-w-0">
          <LocationOffIcon
            size={18}
            className="tw:text-md3-on-surface-variant tw:shrink-0"
          />
          <span className="tw:shrink-0 tw:whitespace-nowrap tw:text-sm tw:leading-5 tw:text-md3-on-surface-variant tw:tracking-tight">
            {Localization.translator.thisLocationIsNotGeolocated}
          </span>
        </div>
      )
    }
    return (
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:h-10 tw:pl-1.5 tw:pr-1.5 tw:py-1.5 tw:bg-white tw:border tw:border-md3-outline-variant tw:rounded-b tw:-mt-px tw:min-w-0">
        <div className="tw:flex tw:items-center tw:gap-1 tw:min-w-0">
          <InfoIcon
            size="sm"
            className="tw:text-md3-on-surface-variant tw:shrink-0"
          />
          <span className="tw:shrink-0 tw:whitespace-nowrap tw:text-sm tw:leading-5 tw:text-md3-on-surface-variant tw:tracking-tight">
            {Localization.translator.inheritsFrom}
          </span>
        </div>
        {this.renderAncestorTag()}
      </div>
    )
  }

  private renderCollapsedBottomControls(): JSX.Element {
    return (
      <div className="tw:flex tw:items-center tw:justify-between tw:h-10 tw:pl-3 tw:pr-1.5 tw:py-1.5 tw:bg-white tw:border tw:border-border tw:rounded-b tw:-mt-px">
        {this.renderCoordinates()}
        {this.canEditLocation && this.renderMoveToLocationButton()}
      </div>
    )
  }

  private renderFooter(): JSX.Element {
    return (
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-2 tw:border-t tw:border-border tw:bg-white tw:px-3 tw:pt-3">
        {this.renderFooterContent()}
      </div>
    )
  }

  // Browser-fullscreen lifts the markup toolbar to clear the bottom Save/Cancel
  // pill; the var is set on the stage and inherits into the markup overlay.
  private get markupToolbarVars(): React.CSSProperties | undefined {
    return this.isBrowserFullscreen
      ? ({ '--markup-toolbar-bottom': '76px' } as React.CSSProperties)
      : undefined
  }

  private renderFullscreenActions(): JSX.Element {
    return (
      <div className="tw:absolute tw:bottom-3 tw:left-1/2 tw:z-[110] tw:flex tw:-translate-x-1/2 tw:items-center tw:gap-2 tw:rounded-lg tw:bg-popover tw:px-3 tw:py-2 tw:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.15),0px_1px_3px_0px_rgba(0,0,0,0.3)]">
        {this.renderFooterContent()}
      </div>
    )
  }

  private renderFooterContent(): JSX.Element {
    if (this.isEditMode) {
      return (
        <MovePinControls
          isMobileEnv={this.props.isMobileEnv}
          onCancel={this.cancelPinEdit}
          onDone={this.exitPinEdit}
          onMoveToMyLocation={
            this.canEditLocation ? this.moveToMyLocation : undefined
          }
        />
      )
    }

    if (this.isMarkupMode) {
      return (
        <>
          <Button variant="outline" size="lg" onClick={this.handleCloseAttempt}>
            {Localization.translator.cancel}
          </Button>
          <Button
            variant="default"
            size="lg"
            disabled={!this.hasUnsavedChanges && !this.hasUnsavedEditorChanges}
            onClick={this.saveEdit}
          >
            {Localization.translator.save}
          </Button>
        </>
      )
    }

    return (
      <Button variant="outline" size="lg" onClick={this.closeMap}>
        {Localization.translator.close}
      </Button>
    )
  }

  private renderMapViewsGallery(): JSX.Element {
    return (
      <div className="scrollable col overflow-hidden full-height">
        <div className="full-height relative">
          <MapViewsGallery
            mapIds={resolvePreciseLocationGlobeIds(this.props).map(map => ({
              globeViewId: map,
            }))}
            selectedGlobeViewId={
              this.globeViewControlStore.selectedGlobeView?.id
            }
            renderMapViewComponent={this.renderMapView}
            shouldDisableZoom={true}
            shouldUseFullHeight={true}
            selectGlobe={this.selectGlobeAndCloseModals}
            isCollapsed={!this.isExpanded}
            mapBoxViewerStore={this.mapBoxViewerStore}
            canReorder={false}
          />
        </div>
      </div>
    )
  }

  private renderMapView = (): JSX.Element => {
    const isPreview = !this.isExpanded
    return (
      <div
        className={classList({
          'full-width full-height relative': true,
          // The canvas carries an inline cursor, which a class can only outrank with !. Mapbox's own
          // controls keep theirs: this reaches the canvas alone.
          'tw:cursor-pointer tw:[&_.mapboxgl-canvas]:cursor-pointer!':
            isPreview,
        })}
        onClick={isPreview ? this.openFromPreview : undefined}
      >
        <FormPreciseLocationMap
          value={this.value}
          mapBoxViewerStore={this.mapBoxViewerStore}
          globe={this.globeViewControlStore.selectedGlobeView}
          fullscreenTargetRef={this.stageRef}
          isExpanded={this.isExpanded}
          isEditMode={this.isEditMode}
          isMarkupMode={this.isMarkupMode}
          canMovePin={this.isMarkupMode && this.canEditLocation}
          pinState={this.pinState}
          collapsed={this.isMoveAffordanceCollapsed}
          onEditPreciseLocation={this.togglePinEdit}
          sitePermitId={this.markupSitePermitId}
          isDeferred={this.isDeferredMarkup}
          pinCoordinates={this.pinCoordinates}
          permitType={this.permitType}
          selectedStartDate={this.startDate}
          selectedEndDate={this.endDate}
          copySelectedId={this.copySelectedMarkupId}
          editorText={this.editorText}
        />
      </div>
    )
  }

  // A saved workflow's pin shows its status and its assignment; one still being written has
  // neither yet and draws as a draft. existingPermit is what separates the two — the editable
  // entity exists in both cases, and in the creation case its status is the one the workflow
  // will be GIVEN rather than one it has, so reading that would paint a new form's pin as
  // Requested. Absent state resolves to the draft grey inside the map.
  @computed
  private get pinState(): IPreciseLocationPinState {
    const workflow = this.localProps.store?.existingPermit
    if (!workflow) return null
    const user = this.localProps.eventsStore?.appState?.user
    return {
      status: workflow.status,
      isLate: workflow.isLate,
      isAssigned: !!user && workflow.isAssignedToUser(user),
    }
  }

  private renderCoordinates(): JSX.Element {
    const { latitude, longitude } = this.mapBoxViewerStore.viewport
    const textClass =
      'tw:text-xs tw:font-medium tw:leading-5 tw:text-foreground tw:whitespace-nowrap tw:py-1'
    if (latitude == null || longitude == null) {
      return (
        <span className={textClass}>
          {Localization.translator.noCoordinatesAvailable}
        </span>
      )
    }
    return (
      <span
        className={textClass}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatCoordinates(latitude, longitude)}
      </span>
    )
  }

  private renderMoveToLocationButton(): JSX.Element {
    return (
      <div
        className="tw:flex tw:items-center tw:gap-1 tw:px-2 tw:py-1 tw:rounded-full tw:cursor-pointer tw:select-none"
        role="button"
        tabIndex={0}
        onClick={this.moveToMyLocation}
        onKeyDown={this.handleMoveToLocationKeyDown}
      >
        <MyLocationIcon size={18} className="tw:text-primary tw:shrink-0" />
        <span className="tw:text-sm tw:font-medium tw:leading-5 tw:text-primary tw:tracking-tight tw:whitespace-nowrap">
          {Localization.translator.moveToMyLocation}
        </span>
      </div>
    )
  }

  private renderSaveSuggestLocationButton(): JSX.Element {
    return (
      <div className="row y-center mw80 mr10">
        <Button
          variant="default"
          className="tw:grow"
          onClick={this.saveSuggestedLocation}
        >
          {Localization.translator.yes}
        </Button>
      </div>
    )
  }

  private renderDismissSuggestLocationButton(): JSX.Element {
    return (
      <div
        className="row y-center pa5 no-grow mr10 pointer"
        onClick={this.declineSuggestedLocation}
      >
        <div className="text extra-large blue w-fit-content nowrap">
          {KEEP_PREVIOUS_LOCATION_TEXT}
        </div>
      </div>
    )
  }

  private initializeState(props: IProps) {
    this.value = props.value
    this.permitType = props.permitType
    this.selectDefaultGlobe(props)
  }

  private selectDefaultGlobe(props: IProps) {
    const globeIds = resolvePreciseLocationGlobeIds(props)
    const bestGlobeId = resolveDefaultGlobeId({
      adoptedMarkupGlobeId: props.store?.adoptedMarkupGlobeId,
      savedGlobeIdFromPreciseLocation:
        props.value?.preciseLocation?.globeViewId,
      locationBasedGlobeId: props.globeViewsStore.findBestGlobeForLocation(
        props.value?.id,
        globeIds,
      ),
      globeIds,
    })
    this.globeViewControlStore.selectGlobeById(bestGlobeId)
  }

  @action.bound
  private updateStateFromProps(prevProps: Readonly<IProps>) {
    if (prevProps.value !== this.props.value) {
      this.value = this.props.value
      if (prevProps.value?.id !== this.props.value?.id) {
        this.closestLocation = null
      }
      if (
        shouldRecomputeDefaultGlobe({
          prevLocationId: prevProps.value?.id,
          nextLocationId: this.props.value?.id,
          isPickerExpanded: this.isExpanded,
        })
      ) {
        this.selectDefaultGlobe(this.props)
      }
    }
    if (prevProps.permitType !== this.props.permitType) {
      this.permitType = this.props.permitType
    }
    // The configured globe list can still be empty at mount right after a project
    // switch, so the one-shot pick resolved nothing. Only fills a gap — it can never
    // override a selection.
    if (
      !this.globeViewControlStore.selectedGlobeViewId &&
      resolvePreciseLocationGlobeIds(this.props).length
    ) {
      this.selectDefaultGlobe(this.props)
    }
  }

  // Enter commits the pin move (same as Done), unless typing or a control is focused.
  private handlePinEditKeyDown = createPinMoveKeyHandler({
    isActive: () => this.isEditMode && this.ownsKeyboard,
    onDone: () => this.exitPinEdit(),
    onCancel: () => this.cancelPinEdit(),
  })

  // Press "A" (desktop) to toggle adjacent-markups visibility while expanded.
  private handleAdjacentMarkupsShortcut = createMarkupsVisibilityKeyHandler({
    isActive: () => this.ownsKeyboard,
    onToggle: () =>
      this.mapBoxViewerStore.markupPrefs.toggleShowAdjacentMarkups(),
  })

  // Several instances stay mounted at once (one per table row), so a missing
  // claim means collapsed, never "base surface".
  private get ownsKeyboard(): boolean {
    return isClaimOnTop(this.keyboardClaim)
  }

  @action.bound
  private releaseKeyboardClaim(): void {
    releaseKeyboard(this.keyboardClaim)
    this.keyboardClaim = null
  }

  @action.bound
  private handleMoveToLocationKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.moveToMyLocation()
    }
  }

  @action.bound
  private handleSetPreciseLocationKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.openPopup()
    }
  }

  @action.bound
  private deletePreciseLocation() {
    this.props.saveLocation?.({
      ...this.value,
      preciseLocation: null,
    })
  }

  @action.bound
  private moveToMyLocation() {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser')
      return
    }

    navigator.geolocation.getCurrentPosition(
      this.handleLocationSuccess,
      this.handleLocationError,
      {
        enableHighAccuracy: true,
        timeout: GEOLOCATION_TIMEOUT,
        maximumAge: GEOLOCATION_MAX_AGE,
      },
    )
  }

  @action.bound
  private handleLocationSuccess(position: GeolocationPosition) {
    const { latitude, longitude } = position.coords

    // Collapsed entry bypasses openPopup; open the edit session so Save is available.
    if (!this.isExpanded) {
      this.beginEditSession()
      this.enterMarkupMode()
    }
    this.enterPinEdit()
    this.mapBoxViewerStore.jumpToViewport({
      latitude,
      longitude,
      zoom: GEOLOCATION_ZOOM_LEVEL,
    })
    // Tracker ignores programmatic moves, so flag the GPS move explicitly.
    this.hasUnsavedChanges = true
  }

  @action.bound
  private handleLocationError(error: GeolocationPositionError) {
    let errorMessage: string
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = Localization.translator.geolocationPermissionDenied
        break
      case error.POSITION_UNAVAILABLE:
        errorMessage = Localization.translator.geolocationPositionUnavailable
        break
      case error.TIMEOUT:
        errorMessage = Localization.translator.geolocationTimeout
        break
      default:
        errorMessage = Localization.translator.geolocationError
        break
    }

    toast.error(errorMessage)
  }

  // Baseline + expand, before any camera move.
  @action.bound
  private beginEditSession() {
    this.collapsedZoom = this.mapBoxViewerStore.viewport.zoom
    this.initialGlobeId = this.globeViewControlStore.selectedGlobeViewId
    const { latitude, longitude, bearing, pitch, zoom } =
      this.mapBoxViewerStore.viewport
    this.initialViewport = { latitude, longitude, bearing, pitch, zoom }
    this.isExpanded = true
    // Overwriting a live claim would orphan it in the stack, leaving the map
    // behind permanently deaf.
    this.releaseKeyboardClaim()
    this.keyboardClaim = claimKeyboard()
    this.hasUnsavedChanges = false
  }

  // Anywhere on the map except mapbox's own chrome: the credits have to stay expandable, and an
  // overlay cannot make that exception — MapBoxViewer's root has its own z-index, so a sibling
  // covering the map covers the corners with it.
  private openFromPreview = (event: React.MouseEvent<HTMLDivElement>): void => {
    if ((event.target as HTMLElement).closest('.mapboxgl-ctrl')) {
      return
    }
    this.openPopup()
  }

  @action.bound
  private openPopup() {
    this.beginEditSession()
    if (this.canEditMarkups) {
      // No location yet → open in pin-drag so it can be placed.
      this.enterMarkupMode()
      if (
        this.canEditLocation &&
        !this.hasPreciseLocation &&
        !this.isLocationGeolocated &&
        !this.inheritedCoordinates
      ) {
        this.enterPinEdit()
      }
    } else {
      this.isEditMode = false
    }
  }

  private startTrackingViewport() {
    this.disposeViewportReaction()
    this.viewportReactionDisposer = startViewportTracking({
      getMapRef: () => this.mapBoxViewerStore.mapRef,
      onMapChange: cb => this.mapBoxViewerStore.onMapChange(cb),
      getInitialViewport: () => this.initialViewport,
      onUnsavedChange: () => {
        this.hasUnsavedChanges = true
      },
    })
  }

  private disposeViewportReaction() {
    if (this.viewportReactionDisposer) {
      this.viewportReactionDisposer()
      this.viewportReactionDisposer = null
    }
  }

  @action.bound
  private handleCloseAttempt() {
    this.exitBrowserFullscreenIfActive()
    if (
      this.isMarkupMode &&
      (this.hasUnsavedEditorChanges || this.hasUnsavedChanges)
    ) {
      this.showMarkupConfirmationDialog = true
    } else {
      this.closeMap()
    }
  }

  @action.bound
  private dismissMarkupConfirmationDialog() {
    this.showMarkupConfirmationDialog = false
  }

  @action.bound
  private discardMarkupsAndClose() {
    this.showMarkupConfirmationDialog = false
    this.cancelEdit()
  }

  @action.bound
  private saveMarkupsAndClose() {
    this.showMarkupConfirmationDialog = false
    this.saveEdit()
  }

  private resetModalState() {
    this.isExpanded = false
    this.releaseKeyboardClaim()
    this.isEditMode = false
    this.isMarkupMode = false
    this.pendingViewport = null
    this.hasUnsavedChanges = false
    this.hasUnsavedEditorChanges = false
    this.isMoveAffordanceCollapsed = false
  }

  private selectGlobeAndCloseModals = (globe: GlobeView) => {
    // The copy source belongs to the previous globe's reference markups; drop it so
    // its Copy pill doesn't linger on the new globe.
    this.setCopySelectedMarkup(null)
    this.globeViewControlStore.selectGlobe(globe)
  }

  private closeMap = () => {
    this.disposeViewportReaction()
    this.mapBoxViewerStore.mapRef?.stop()
    this.restoreInitialGlobe()
    this.restoreInitialViewport()
    this.restoreCollapsedZoom()
    this.resetModalState()
  }

  private restoreInitialGlobe = () => {
    if (
      this.initialGlobeId &&
      this.initialGlobeId !== this.globeViewControlStore.selectedGlobeViewId
    ) {
      this.globeViewControlStore.selectGlobeById(this.initialGlobeId)
    }
  }

  private restoreInitialViewport = () => {
    if (this.initialViewport) {
      this.mapBoxViewerStore.jumpToViewport(this.initialViewport)
    }
  }

  @action.bound
  private saveMapLocation(
    camera = this.pendingViewport ??
      (hasPreciseCoordinates(this.value?.preciseLocation)
        ? this.value.preciseLocation
        : null),
  ) {
    if (!camera) {
      this.exitMarkupMode()
      this.closeMap()
      return
    }
    const { latitude, longitude, bearing, pitch, zoom } = camera
    const globeViewId = this.globeViewControlStore.selectedGlobeView?.id
    const moved = {
      latitude,
      longitude,
      bearing,
      pitch,
      zoom,
      globeViewId,
      // Clear stale screenshots — the save-time capture will set fresh ones.
      // Failing safe to "no image" is better than "wrong image" if capture fails.
      // Use null (not undefined) so the value is included in the JSON payload
      // and actually clears the server-side field.
      imageUrl: null,
      overviewImageUrl: null,
    } as IPreciseLocation
    this.resetModalState()
    if (this.isPinLocationField) {
      this.closestLocation = this.props.store.applyPreciseLocationFromMap(moved)
      return
    }
    this.closestLocation =
      this.props.store.workflowFieldsStore.resolveClosestAllowedLocation(
        this.props.typeField,
        { latitude, longitude, globeViewId },
      )
    this.props.saveLocation?.({
      ...this.value,
      preciseLocation: { ...this.value?.preciseLocation, ...moved },
    })
  }

  private captureScreenshot = async (): Promise<{
    imageUrl?: string
    overviewImageUrl?: string
  }> => {
    const { fileUploadingStore, eventsStore } = this.props
    const map = this.mapBoxViewerStore?.mapRef?.getMap?.()
    if (!fileUploadingStore || !map) {
      return {}
    }

    const pl = this.value?.preciseLocation
    const coordinates = hasPreciseCoordinates(pl)
      ? pl
      : this.mapBoxViewerStore.viewport

    // The thumbnail's own resolver, so the print cannot drift from the preview.
    const globe = this.mapBoxViewerStore.selectedGlobe
    const framed = resolveViewportForPreciseLocation({
      preciseLocation: pl,
      selectedGlobe: globe,
      currentViewport: this.mapBoxViewerStore.viewport,
    })

    return captureMapScreenshots({
      map,
      fileUploadingStore,
      projectId: eventsStore.appState.activeProject.id,
      preciseViewport: {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        zoom: framed?.zoom ?? preciseLocationZoom(pl?.zoom),
        bearing:
          framed?.bearing ??
          globe?.bearing ??
          this.mapBoxViewerStore.viewport.bearing,
        pitch:
          framed?.pitch ??
          globe?.pitch ??
          this.mapBoxViewerStore.viewport.pitch,
      },
      cropSize: { width: CAPTURE_WIDTH, height: CAPTURE_HEIGHT },
      isSatellite: this.mapBoxViewerStore.isSatelliteActive,
    })
  }

  /**
   * Captures screenshots and writes the URLs directly to this instance's
   * ISiteLocation value. Called by the form store at save time — each
   * component instance handles its own location, so table rows with
   * different pins each get their own screenshots.
   */
  private captureAndApplyScreenshot = async (): Promise<void> => {
    if (!this.value) {
      return
    }

    // Capture whenever the mini-map is displaying a pin: a user-placed precise
    // location OR one resolved from the location's georeference. Without this,
    // workflows on geolocated locations get no map image in PDF reports.
    const value = this.value
    if (hasPreciseCoordinates(value.preciseLocation)) {
      const globeViewId = resolvePinGlobeId({
        savedGlobeId: value.preciseLocation.globeViewId,
        displayedGlobeId: this.mapBoxViewerStore.selectedGlobe?.id,
        globeIds: resolvePreciseLocationGlobeIds({ ...this.props, value }),
      })
      if (globeViewId !== value.preciseLocation.globeViewId) {
        value.preciseLocation.globeViewId = globeViewId
        clearSnapshotUrls(value)
      }
    }
    const action = decideSnapshotAction({
      hasPin: hasPreciseCoordinates(value.preciseLocation),
      isResolvable: this.isLocationGeolocated || !!this.inheritedCoordinates,
      // Both stores feed resolvability: sitemap items directly, location
      // attributes through the ancestor-inheritance chain — an unhydrated
      // half must not let clear-stale wipe good images.
      isLocationDataSettled:
        !!this.localProps.sitemapItemsStore?.isDataReceived &&
        !!this.localProps.locationAttributesStore?.isDataReceived,
      hasRecord: !!value.preciseLocation,
      hasCompleteSnapshot: hasCompleteSnapshot(value.preciseLocation),
    })
    if (action === 'none' || action === 'skip') {
      return
    }
    if (action === 'clear-stale') {
      clearSnapshotUrls(value)
      return
    }

    const recordBefore = value.preciseLocation
    // The map mounts a task after the form paints, so a save in that window finds it absent or
    // still loading; the precise shot is taken without an idle wait of its own.
    this.mountMap()
    // Capturing anyway would upload a blank canvas and report success, which is the case the wait
    // exists for.
    if (
      this.isMapSubtreeRendered &&
      !(await waitForCaptureReadyMap(
        () => this.mapBoxViewerStore?.mapRef?.getMap?.(),
        MAP_CAPTURE_WAIT_MS,
      ))
    ) {
      toast.error(CAPTURE_FAILED_MESSAGE)
      return
    }
    const result = await this.captureScreenshot()
    // The pin may have been deleted or replaced during the capture window —
    // results for a superseded record must not land on the live value.
    if (this.value !== value || value.preciseLocation !== recordBefore) {
      return
    }

    // Warn the user if capture failed — the save still proceeds but the
    // PDF report won't have map images for this location. The record is only
    // materialized on success, so a failed capture cannot persist a junk
    // preciseLocation where the stored value was null.
    if (!result.imageUrl && !result.overviewImageUrl) {
      toast.error(CAPTURE_FAILED_MESSAGE)
      return
    }

    if (!value.preciseLocation) {
      // Image-only record: coordinates AND globeViewId stay unset so no pin is
      // materialized and no globe-scoped map filter picks it up — the report
      // renders images independently of both.
      value.preciseLocation = {} as ISiteLocation['preciseLocation']
    }
    const merged = mergeSnapshotResult(recordBefore, result)
    value.preciseLocation.imageUrl = merged.imageUrl
    value.preciseLocation.overviewImageUrl = merged.overviewImageUrl
  }

  private saveSuggestedLocation = () => {
    this.props.saveLocation?.({
      ...this.value,
      id: this.closestLocation.id,
      type: this.closestLocation.type,
    })
    this.cleanClosestLocation()
  }

  @action.bound
  private declineSuggestedLocation() {
    if (this.isPinLocationField) {
      this.props.store.workflowFieldsStore.declinePinLocation(
        this.closestLocation.id,
      )
    }
    this.cleanClosestLocation()
  }

  @action.bound
  private cleanClosestLocation() {
    this.closestLocation = null
  }

  private resetViewport = (animate?: boolean) => {
    if (hasPreciseCoordinates(this.props.value?.preciseLocation)) {
      if (animate) {
        const { latitude, longitude, zoom, pitch, bearing } =
          this.props.value.preciseLocation
        this.mapBoxViewerStore.mapRef?.flyTo({
          center: [longitude, latitude],
          zoom: preciseLocationZoom(zoom),
          pitch: pitch ?? this.mapBoxViewerStore.viewport.pitch,
          bearing: bearing ?? this.mapBoxViewerStore.viewport.bearing,
          essential: true,
        })
      } else {
        // Explicit pose, as the animate branch above: a map-seeded record carries no camera,
        // and a null zoom lands mapbox on the globe.
        const { latitude, longitude, zoom, pitch, bearing } =
          this.props.value.preciseLocation
        this.mapBoxViewerStore.jumpToViewport({
          latitude,
          longitude,
          zoom: preciseLocationZoom(zoom),
          pitch: pitch ?? this.mapBoxViewerStore.viewport.pitch,
          bearing: bearing ?? this.mapBoxViewerStore.viewport.bearing,
        })
      }
    } else if (this.props.value?.id) {
      if (animate) {
        const coords = this.inheritedCoordinates
        if (coords) {
          this.mapBoxViewerStore.setViewportFromCoordinates(coords)
          return
        }
        this.mapBoxViewerStore.selectViewportFromLocationAttributeId(
          this.props.value.id,
        )
      } else {
        const coords = this.inheritedCoordinates
        if (coords) {
          this.mapBoxViewerStore.jumpToViewport({
            latitude: coords.latitude,
            longitude: coords.longitude,
          })
        }
      }
    }
  }

  private restoreCollapsedZoom() {
    if (this.collapsedZoom != null) {
      this.mapBoxViewerStore.jumpToViewport({ zoom: this.collapsedZoom })
    }
  }

  private get startDate(): Date {
    const startDate =
      this.localProps.store?.workflowFieldsStore?.editableEntity?.startDate
    return startDate ? new Date(startDate) : new Date()
  }
  private get endDate(): Date {
    const endDate =
      this.localProps.store?.workflowFieldsStore?.editableEntity?.endDate
    return endDate ? new Date(endDate) : new Date()
  }

  private markInsideTree = (event: React.MouseEvent) => {
    this.insideTreeEvent = event.nativeEvent
  }

  private handleClickOutside = (event: MouseEvent) => {
    const isInsideTree = event === this.insideTreeEvent
    this.insideTreeEvent = null

    if (this.localProps.isMobileEnv || !this.isExpanded) return
    if (
      isInsideTree ||
      !isPreciseLocationOutsideClick(event.target, this.containerRef.current)
    ) {
      return
    }
    event.stopPropagation()
    event.preventDefault()
    this.handleCloseAttempt()
  }
}
